import React from "react";
import {
  getPstOffset,
  getRelativeDate,
  serviceToDisplay,
} from "../../util/dateUtils";
import {
  Textarea,
  Multiselect,
  SelectProps,
  DateRangePicker,
  NonCancelableCustomEvent,
} from "@amzn/awsui-components-react/polaris";
import {
  setAttributeQuery,
  Range,
  unsetAttributeQuery,
} from "./searchSideBarConnector";
import {
  DATE_RANGE_I18N,
  DATE_RANGE_RELATIVE_OPTIONS,
} from "./SearchSideBar-config";
import { DateRangePickerProps } from "@amzn/awsui-components-react/polaris/date-range-picker/interfaces";

function optionsFromCsv(labelGetter: (value: string) => string, csv?: string) {
  if (csv === undefined) {
    return [];
  }
  const values = csv.split(",").map((s) => s.trim());
  return values
    .filter((value) => value !== "")
    .map((value) => ({
      value: value,
      label: labelGetter(value),
    }));
}

export const StringField = (
  placeholder: string,
  attributeName: string,
  value: string,
  dispatch: any
) => {
  return (
    <Textarea
      placeholder={placeholder}
      onChange={(event) => {
        dispatch(
          setAttributeQuery({
            attribute: attributeName,
            queryState: {
              value: event.detail.value,
            },
          })
        );
      }}
      value={value}
    />
  );
};

export const MultiselectField = (
  options: SelectProps.Options,
  placeholder: string,
  attributeName: string,
  value: string,
  dispatch: any,
  status?: "pending" | "loading" | "finished" | "error",
  loadingText?: string,
  retryFunction?: any,
  labelGetter: (value: string) => string = (v) => v,
  virtualScroll: boolean = false
) => {
  return (
    <Multiselect
      virtualScroll={options.length >= 500 || virtualScroll}
      statusType={status}
      loadingText={loadingText}
      placeholder={placeholder}
      filteringType="auto"
      options={options}
      selectedOptions={optionsFromCsv(labelGetter, value)}
      onChange={(event) => {
        const serializedOptions = event.detail.selectedOptions
          .map((o) => o.value)
          .join(",");
        dispatch(
          setAttributeQuery({
            attribute: attributeName,
            queryState: {
              value: serializedOptions,
            },
          })
        );
      }}
      errorText={"Something went wrong."}
      recoveryText={"Retry"}
      onLoadItems={({ detail }) => {
        if (detail.samePage && retryFunction) {
          dispatch(retryFunction());
        }
      }}
    />
  );
};

export const DateRangeField = (
  attributeName: string,
  dispatch: any,
  value?: Range
) => {
  const handleClear = (): void => {
    dispatch(unsetAttributeQuery({ attribute: attributeName }));
  };

  const handleAbsoluteRange = (
    value: DateRangePickerProps.AbsoluteValue
  ): void => {
    const [startDate, endDate] = [
      new Date(value.startDate),
      new Date(value.endDate),
    ].map((value: Date) => {
      return getRelativeDate(value, -getPstOffset(value), "minute");
    });
    dispatch(
      setAttributeQuery({
        attribute: attributeName,
        queryState: {
          value: {
            // Start of startDate 2022-04-28, 2022-04-28T07:00:00.000Z
            lower: startDate,
            // End of endDate 2022-04-28, 2022-04-29T06:59:59.000Z
            upper: getRelativeDate(endDate, 24 * 60 * 60 - 1, "second"),
          },
        },
      })
    );
  };

  const handleRelativeRange = (
    value: DateRangePickerProps.RelativeValue
  ): void => {
    const now = new Date();
    const relativeDate = getRelativeDate(now, value.amount, value.unit);
    dispatch(
      setAttributeQuery({
        attribute: attributeName,
        queryState: {
          value:
            value.amount > 0
              ? { lower: now, upper: relativeDate }
              : { lower: relativeDate, upper: now },
        },
      })
    );
  };

  const handleDateRangePickerChange = () => {
    return (
      event: NonCancelableCustomEvent<DateRangePickerProps.ChangeDetail>
    ): void => {
      const dateValue = event.detail.value;
      if (dateValue === null) {
        handleClear();
        return;
      }
      switch (dateValue.type) {
        case "absolute": {
          handleAbsoluteRange(dateValue);
          break;
        }
        case "relative": {
          handleRelativeRange(dateValue);
          break;
        }
      }
    };
  };

  const handleValidation = (): DateRangePickerProps.ValidationFunction => {
    return (
      range: DateRangePickerProps.Value | null
    ): DateRangePickerProps.ValidationResult => {
      if (range?.type === "absolute") {
        const [startDateWithoutTime] = range.startDate.split("T");
        const [endDateWithoutTime] = range.endDate.split("T");

        if (!startDateWithoutTime || !endDateWithoutTime) {
          return {
            valid: false,
            errorMessage:
              "The selected date range is incomplete. Select a start and end date for the date range.",
          };
        }
      } else if (range?.type === "relative") {
        if (isNaN(range.amount)) {
          return {
            valid: false,
            errorMessage:
              "The selected date range is incomplete. Specify a duration for the date range.",
          };
        }
      }
      return { valid: true };
    };
  };

  const getDateRangePickerValue = (): DateRangePickerProps.Value => {
    return {
      startDate: value ? serviceToDisplay(value.lower, "yyyy-MM-DD") : "",
      endDate: value ? serviceToDisplay(value.upper, "yyyy-MM-DD") : "",
      type: "absolute",
    };
  };

  return (
    <div>
      <DateRangePicker
        expandToViewport
        dateOnly
        value={getDateRangePickerValue()}
        relativeOptions={DATE_RANGE_RELATIVE_OPTIONS}
        isValidRange={handleValidation()}
        i18nStrings={DATE_RANGE_I18N}
        onChange={handleDateRangePickerChange()}
        timeOffset={0}
      />
    </div>
  );
};
